import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

type Todo = {
  id: string;
  project: string;
  title: string;
  detail: string | null;
  status: 'open' | 'done';
  sort_order: number;
  done_at: string | null;
  created_at: string;
};

type Filter = 'open' | 'all' | 'done';

// Seed groups keep a deliberate order (launch work first); anything Laura or
// Leah adds under a new project name falls in after, alphabetically.
const GROUP_RANK: Record<string, number> = {
  'SMS launch': 0,
  'SMS follow-ups': 1,
  Apps: 2,
  Backbar: 3,
};
const groupRank = (p: string) => GROUP_RANK[p] ?? 50;

export function Todos() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Todo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('open');
  const [newProject, setNewProject] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDetail, setNewDetail] = useState('');
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data, error: err } = await supabase
      .from('backbar_todos')
      .select('id, project, title, detail, status, sort_order, done_at, created_at')
      .order('project')
      .order('sort_order')
      .order('created_at');
    if (err) {
      setError(err.message);
      setRows([]);
      return;
    }
    setError(null);
    setRows((data ?? []) as Todo[]);
  }
  useEffect(() => {
    void load();
  }, []);

  async function toggle(t: Todo) {
    const toDone = t.status === 'open';
    // optimistic — the row flips immediately, reverts on error
    setRows((rs) =>
      (rs ?? []).map((r) =>
        r.id === t.id
          ? { ...r, status: toDone ? 'done' : 'open', done_at: toDone ? new Date().toISOString() : null }
          : r,
      ),
    );
    const { error: err } = await supabase
      .from('backbar_todos')
      .update(
        toDone
          ? { status: 'done', done_at: new Date().toISOString(), done_by: user?.id ?? null }
          : { status: 'open', done_at: null, done_by: null },
      )
      .eq('id', t.id);
    if (err) {
      setError(err.message);
      void load();
    }
  }

  async function remove(t: Todo) {
    if (!window.confirm(`Remove "${t.title}"?`)) return;
    setRows((rs) => (rs ?? []).filter((r) => r.id !== t.id));
    const { error: err } = await supabase.from('backbar_todos').delete().eq('id', t.id);
    if (err) {
      setError(err.message);
      void load();
    }
  }

  async function add() {
    const project = newProject.trim();
    const title = newTitle.trim();
    if (!project || !title) return;
    setSaving(true);
    const siblings = (rows ?? []).filter((r) => r.project === project);
    const nextSort = siblings.length ? Math.max(...siblings.map((r) => r.sort_order)) + 10 : 10;
    const { error: err } = await supabase.from('backbar_todos').insert({
      project,
      title,
      detail: newDetail.trim() || null,
      sort_order: nextSort,
    });
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setNewTitle('');
    setNewDetail('');
    void load();
  }

  const groups = useMemo(() => {
    const byProject = new Map<string, Todo[]>();
    for (const r of rows ?? []) {
      const list = byProject.get(r.project) ?? [];
      list.push(r);
      byProject.set(r.project, list);
    }
    return [...byProject.entries()].sort(
      ([a], [b]) => groupRank(a) - groupRank(b) || a.localeCompare(b),
    );
  }, [rows]);

  const projects = useMemo(() => groups.map(([p]) => p), [groups]);
  const openTotal = (rows ?? []).filter((r) => r.status === 'open').length;
  const doneTotal = (rows ?? []).length - openTotal;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Backbar · Operations</div>
          <h1 className="page-title">To-Do</h1>
          <p className="page-sub">
            The shared punch list — {openTotal} open · {doneTotal} done. Checks are visible to
            every admin.
          </p>
        </div>
        <div className="seg">
          {(['open', 'all', 'done'] as Filter[]).map((f) => (
            <button key={f} className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
              {f === 'open' ? 'Open' : f === 'all' ? 'All' : 'Done'}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <span className="chip chip--danger">Couldn’t save</span>{' '}
          <span className="muted">{error}</span>
        </div>
      )}

      <section className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-head">
          <span className="panel-title">Add a task</span>
        </div>
        <div className="todo-add">
          <input
            className="inp"
            style={{ maxWidth: 200 }}
            list="todo-projects"
            placeholder="Project"
            value={newProject}
            onChange={(e) => setNewProject(e.target.value)}
          />
          <datalist id="todo-projects">
            {projects.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
          <input
            className="inp"
            style={{ flex: 1, minWidth: 220 }}
            placeholder="What needs doing?"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void add()}
          />
          <input
            className="inp"
            style={{ flex: 2, minWidth: 260 }}
            placeholder="Detail (optional)"
            value={newDetail}
            onChange={(e) => setNewDetail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void add()}
          />
          <button
            className="btn btn--primary"
            disabled={saving || !newProject.trim() || !newTitle.trim()}
            onClick={() => void add()}
          >
            Add
          </button>
        </div>
      </section>

      {rows === null && <div className="muted">Loading…</div>}
      {rows !== null && groups.length === 0 && (
        <div className="panel">
          <span className="muted">Nothing on the list. Add the first task above.</span>
        </div>
      )}

      {groups.map(([project, items]) => {
        const done = items.filter((r) => r.status === 'done').length;
        const shown = items.filter((r) =>
          filter === 'all' ? true : filter === 'open' ? r.status === 'open' : r.status === 'done',
        );
        if (shown.length === 0 && filter !== 'all') return null;
        return (
          <section className="panel" key={project} style={{ marginBottom: 16 }}>
            <div className="panel-head">
              <span className="panel-title">{project}</span>
              <span className={`chip ${done === items.length ? 'chip--ok' : 'chip--info'}`}>
                {done}/{items.length} done
              </span>
            </div>
            <div className="bar" style={{ marginBottom: 8 }}>
              <i style={{ width: `${items.length ? Math.round((done / items.length) * 100) : 0}%` }} />
            </div>
            {shown.map((t) => (
              <div className="todo-row" key={t.id}>
                <input
                  type="checkbox"
                  className="todo-check"
                  checked={t.status === 'done'}
                  onChange={() => void toggle(t)}
                  aria-label={`Mark "${t.title}" ${t.status === 'open' ? 'done' : 'open'}`}
                />
                <div className="todo-body">
                  <div className={`todo-title${t.status === 'done' ? ' done' : ''}`}>{t.title}</div>
                  {t.detail && <div className="todo-detail">{t.detail}</div>}
                </div>
                <button className="btn btn--ghost todo-x" title="Remove" onClick={() => void remove(t)}>
                  ×
                </button>
              </div>
            ))}
          </section>
        );
      })}
    </>
  );
}
